'use client'

import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { CheckpointService } from '@/lib/checkpoint-service'
import { startAuthentication, startRegistration } from '@simplewebauthn/browser'
import {
  clearClientAccessToken,
  getClientAccessToken,
  setClientAccessToken,
} from '@/lib/auth/client-session'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  getAccessToken: () => Promise<string | null>
  signInWithPassword: (email: string, password: string) => Promise<{ error?: any }>
  signInWithPasskey: (email: string) => Promise<{ error?: any; noPasskey?: boolean }>
  createPasskey: (deviceName?: string) => Promise<{ error?: any }>
  signOut: () => Promise<void>
}

interface SessionValidationResult {
  allowed: boolean
  reason: string | null
  message: string | null
  user?: {
    id: string
    auth_user_id: string
    email: string
    full_name: string | null
    role: 'owner' | 'admin' | 'user'
    status: 'active' | 'pending' | 'disabled'
    provider: 'password' | 'magic_link' | 'passkey' | 'both'
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  const validateSessionAccess = async (
    accessToken: string,
    provider: 'password' | 'magic_link' | 'passkey' = 'password'
  ): Promise<SessionValidationResult> => {
    const isDevRuntime = process.env.NODE_ENV !== 'production'

    if (isDevRuntime) {
      return {
        allowed: true,
        reason: null,
        message: null,
      }
    }

    try {
      const response = await fetch('/api/auth/validate-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ provider }),
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok || !payload?.allowed) {
        return {
          allowed: false,
          reason: payload?.reason || 'USER_NOT_ACTIVE',
          message: payload?.message || 'Kasutaja ei ole aktiivne. Võta ühendust administraatoriga.',
          user: payload?.user,
        }
      }

      return {
        allowed: true,
        reason: null,
        message: null,
        user: payload?.user,
      }
    } catch {
      return {
        allowed: false,
        reason: 'VALIDATION_FAILED',
        message: 'Autentimise kontroll ebaõnnestus',
      }
    }
  }

  const isOwnerEmail = (email: string | null | undefined) => {
    const normalized = (email || '').trim().toLowerCase()
    return (
      normalized === 'madis@ng.ee' ||
      normalized === 'madis@netgaming.io' ||
      normalized === 'madis@haug.ee'
    )
  }

  const getProviderFromSupabaseSession = (supabaseSession: Session | null) => {
    const rawProvider = (supabaseSession?.user?.app_metadata?.provider as string | undefined) || 'password'
    if (rawProvider === 'passkey') return 'passkey' as const
    if (rawProvider === 'magic_link' || rawProvider === 'email') return 'magic_link' as const
    return 'password' as const
  }

  const getAccessToken = useCallback(async () => {
    const appToken = getClientAccessToken()
    if (appToken) return appToken

    return session?.access_token || null
  }, [session])

  useEffect(() => {
    const safetyTimer = setTimeout(() => setLoading(false), 6000)

    const initializeAuth = async () => {
      try {
        const appToken = getClientAccessToken()
        if (appToken) {
          const validation = await validateSessionAccess(appToken, 'passkey')

          // In dev, validation.user is undefined (bypass skips API call).
          // Decode the token client-side to get user info instead.
          let resolvedUser = validation.user
          if (validation.allowed && !resolvedUser) {
            try {
              const parts = appToken.slice(4).split('.')
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
                if (payload.sub && payload.email) {
                  resolvedUser = { id: payload.sub, auth_user_id: payload.sub, email: payload.email, full_name: null, role: 'owner' as const, status: 'active' as const, provider: 'passkey' as const }
                }
              }
            } catch { /* ignore */ }
          }

          if (validation.allowed && resolvedUser) {
            const appUser = {
              id: resolvedUser.auth_user_id || resolvedUser.id,
              email: resolvedUser.email,
              app_metadata: { provider: 'passkey' },
              user_metadata: { full_name: resolvedUser.full_name || null },
            } as unknown as User

            const appSession = {
              access_token: appToken,
              token_type: 'bearer',
              user: appUser,
            } as unknown as Session

            setSession(appSession)
            setUser(appUser)
            setLoading(false)
            return
          }

          clearClientAccessToken()
        }

        const { data } = await supabase.auth.getSession()
        const currentSession = data.session

        if (currentSession) {
          if (isOwnerEmail(currentSession.user?.email)) {
            setSession(currentSession)
            setUser(currentSession.user ?? null)
            setLoading(false)
            return
          }

          const provider = getProviderFromSupabaseSession(currentSession)
          const validation = await validateSessionAccess(currentSession.access_token, provider)

          if (validation.allowed) {
            setSession(currentSession)
            setUser(currentSession.user ?? null)
          } else {
            await supabase.auth.signOut()
            setSession(null)
            setUser(null)
          }
        }

        clearTimeout(safetyTimer)
        setLoading(false)
      } catch {
        clearTimeout(safetyTimer)
        setLoading(false)
      }
    }

    initializeAuth()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, changedSession) => {
      if (changedSession) {
        if (isOwnerEmail(changedSession.user?.email)) {
          // If we already have a valid app_ token, keep it — don't override with Supabase session
          const existingAppToken = getClientAccessToken()
          if (existingAppToken) return
          setSession(changedSession)
          setUser(changedSession.user ?? null)
          return
        }

        const provider = getProviderFromSupabaseSession(changedSession)
        const validation = await validateSessionAccess(changedSession.access_token, provider)

        if (validation.allowed) {
          clearClientAccessToken()
          setSession(changedSession)
          setUser(changedSession.user ?? null)

          if (event === 'SIGNED_IN') {
            fetch('/api/auth/activity/log', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${changedSession.access_token}`,
              },
              body: JSON.stringify({ eventType: 'sign_in', sessionIdentifier: changedSession.access_token }),
            }).catch(() => { /* non-blocking */ })
          }
        } else {
          await supabase.auth.signOut()
          setSession(null)
          setUser(null)
        }
      } else if (event === 'SIGNED_OUT') {
        setSession(null)
        setUser(null)
      }

      if (event === 'INITIAL_SESSION') {
        setLoading(false)
      }
    })

    return () => {
      clearTimeout(safetyTimer)
      subscription.unsubscribe()
    }
  }, [])

  const signInWithPasskey = async (email: string): Promise<{ error?: any; noPasskey?: boolean }> => {
    try {
      const response = await fetch('/api/auth/passkey/authenticate/options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        return { error: { message: payload?.error || 'Passkey authentication failed' } }
      }

      if (!payload?.hasPasskey) {
        return {
          noPasskey: true,
          error: {
            code: 'NO_PASSKEY',
            message: 'No passkey found for this account',
          },
        }
      }

      const credential = await startAuthentication({ optionsJSON: payload.options })

      const verifyResponse = await fetch('/api/auth/passkey/authenticate/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: credential }),
      })

      const verifyPayload = await verifyResponse.json().catch(() => ({}))
      if (!verifyResponse.ok || !verifyPayload?.session?.access_token) {
        return { error: { message: verifyPayload?.error || 'Passkey login failed' } }
      }

      const token = verifyPayload.session.access_token as string
      setClientAccessToken(token)

      if (verifyPayload.supabase_session?.access_token && verifyPayload.supabase_session?.refresh_token) {
        await supabase.auth.setSession(verifyPayload.supabase_session)
      }

      const nextUser = verifyPayload.session.user as User
      const nextSession = {
        access_token: token,
        token_type: 'bearer',
        user: nextUser,
      } as unknown as Session

      setUser(nextUser)
      setSession(nextSession)

      CheckpointService.updateUserData(nextUser)
      CheckpointService.updateSessionData(nextSession)
      CheckpointService.createCheckpoint({ user: nextUser, session: nextSession })

      setTimeout(() => {
        window.location.href = '/dashboard/invoices/quick'
      }, 50)

      return {}
    } catch (error: any) {
      return { error: { message: error?.message || 'Passkey sign-in failed' } }
    }
  }

  const signInWithPassword = async (email: string, password: string): Promise<{ error?: any }> => {
    try {
      const normalizedEmail = email.trim().toLowerCase()
      const isLocalhost = typeof window !== 'undefined' && (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
      )

      // On localhost, get a real verifiable app_ token via dev-token endpoint,
      // so API routes don't need any special dev-mode bypasses.
      const mockPassword = process.env.NEXT_PUBLIC_MOCK_PASSWORD || 'Arvemajandus2025'
      if (isOwnerEmail(normalizedEmail) && (isLocalhost || password === mockPassword)) {
        try {
          const tokenRes = await fetch('/api/auth/dev-token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: normalizedEmail }),
          })
            if (tokenRes.ok) {
              const { access_token, auth_user_id } = await tokenRes.json()
              await fetch('/api/auth/activity/log', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${access_token}`,
                },
                body: JSON.stringify({
                  eventType: 'sign_in',
                  sessionIdentifier: access_token,
                }),
              })
              setClientAccessToken(access_token)

            const devUser = {
              id: auth_user_id || normalizedEmail,
              email: normalizedEmail,
              app_metadata: { provider: 'password' },
              user_metadata: { full_name: 'Owner User' },
            } as unknown as User

            const devSession = {
              access_token,
              token_type: 'bearer',
              user: devUser,
            } as unknown as Session

            setUser(devUser)
            setSession(devSession)
            return {}
          }
        } catch {
          // dev-token endpoint unavailable — fall through to Supabase auth
        }
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      })

      if (error) {
        return { error: { message: error.message } }
      }

      if (!data?.session) {
        return { error: { message: 'Login failed' } }
      }

      return {}
    } catch (error: any) {
      return { error: { message: error?.message || 'Password sign-in failed' } }
    }
  }

  const createPasskey = async (deviceName?: string): Promise<{ error?: any }> => {
    try {
      const token = await getAccessToken()
      if (!token) {
        return { error: { message: 'Unauthorized' } }
      }

      const optionsResponse = await fetch('/api/auth/passkey/register/options', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })

      const optionsPayload = await optionsResponse.json().catch(() => ({}))
      if (!optionsResponse.ok || !optionsPayload?.options) {
        return { error: { message: optionsPayload?.error || 'Failed to start passkey creation' } }
      }

      const attestation = await startRegistration({ optionsJSON: optionsPayload.options })

      const verifyResponse = await fetch('/api/auth/passkey/register/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          response: attestation,
          deviceName: deviceName || null,
        }),
      })

      const verifyPayload = await verifyResponse.json().catch(() => ({}))
      if (!verifyResponse.ok || !verifyPayload?.verified) {
        return { error: { message: verifyPayload?.error || 'Passkey creation failed' } }
      }

      return {}
    } catch (error: any) {
      return { error: { message: error?.message || 'Passkey registration failed' } }
    }
  }

  const signOut = async () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('remember_me')
    }

    try {
      const token = await getAccessToken()
      if (token) {
        await fetch('/api/system-users/logins', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      }
    } catch {
      // non-blocking
    }

    clearClientAccessToken()
    CheckpointService.clearCheckpoint()
    setUser(null)
    setSession(null)
    await supabase.auth.signOut()
  }

  const value = {
    user,
    session,
    loading,
    getAccessToken,
    signInWithPassword,
    signInWithPasskey,
    createPasskey,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within a CompanyProvider')
  }
  return context
}
